"use client"

import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { ArrowLeft } from "lucide-react"
import { MobileBottomNav } from "../components/MobileBottomNav"
import { useAuth } from "../hooks/useAuth"
import Footer from "../components/Footer"
import { motion } from "framer-motion"

export default function PrivacyPolicyPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [activeSection, setActiveSection] = useState("introduction")

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  }

  const sections = [
    { id: "introduction", title: "Introduction" },
    { id: "information", title: "Information We Collect" },
    { id: "usage", title: "How We Use Information" },
    { id: "sharing", title: "Information Sharing" },
    { id: "cookies", title: "Cookies & Tracking" },
    { id: "security", title: "Data Security" },
    { id: "rights", title: "Your Rights" },
    { id: "children", title: "Children's Privacy" },
    { id: "international", title: "International Transfers" },
    { id: "changes", title: "Changes to Policy" },
    { id: "contact", title: "Contact Us" },
  ]

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: "smooth" })
      setActiveSection(sectionId)
    }
  }

  // Track scroll position to update active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100

      sections.forEach(({ id }) => {
        const element = document.getElementById(id)
        if (element) {
          const { offsetTop, offsetHeight } = element
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(id)
          }
        }
      })
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  return (
    <div className="min-h-screen bg-[#EDEFEF]">
      {/* Header */}
      {isMobile ? (
        <header className="bg-black text-white p-4 sticky top-0 z-50">
          <div className="flex items-center">
            <button onClick={() => navigate(-1)} className="mr-4">
              <ArrowLeft className="h-6 w-6" />
            </button>
            <h1 className="text-xl font-bold">Privacy Policy</h1>
          </div>
        </header>
      ) : (
        <header className="bg-black text-white p-4 sticky top-0 z-50">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex justify-between items-center">
              <Link to="/">
                <h1 className="text-2xl font-bold">VillaKosh</h1>
              </Link>
              <nav className="flex items-center space-x-4">
                <Link to="/" className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out">
                  Home
                </Link>
                <Link to="/about" className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out">
                  About
                </Link>
                <Link to="/contact" className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out">
                  Contact
                </Link>
              </nav>
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      <motion.main
        className={`container mx-auto px-4 py-8 ${isMobile ? "pb-24" : ""}`}
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar for desktop */}
          {!isMobile && (
            <motion.aside className="md:w-1/4 lg:w-1/5" variants={itemVariants}>
              <div className="bg-white rounded-lg shadow-md p-4 sticky top-24">
                <h3 className="text-lg font-semibold mb-4">Table of Contents</h3>
                <nav>
                  <ul className="space-y-2">
                    {sections.map((section) => (
                      <li key={section.id}>
                        <button
                          onClick={() => scrollToSection(section.id)}
                          className={`text-left w-full px-3 py-2 rounded-md transition-colors ${
                            activeSection === section.id
                              ? "bg-gray-100 text-black font-medium"
                              : "text-gray-600 hover:bg-gray-50"
                          }`}
                        >
                          {section.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </motion.aside>
          )}

          {/* Main content */}
          <motion.div className="md:w-3/4 lg:w-4/5" variants={itemVariants}>
            <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
              <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
              <p className="text-gray-600 mb-8">Last Updated: April 1, 2025</p>

              <section id="introduction" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
                <p className="text-gray-700 mb-4">
                  VillaKosh ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains
                  how we collect, use, disclose, and safeguard your information when you visit our website, mobile
                  application, and use our services.
                </p>
                <p className="text-gray-700 mb-4">
                  We value your trust and strive to be transparent about our data practices. Please read this policy
                  carefully to understand our practices regarding your personal data and how we will treat it.
                </p>
                <p className="text-gray-700">
                  By accessing or using VillaKosh, you agree to this Privacy Policy. If you do not agree with our
                  policies and practices, please do not use our services.
                </p>
              </section>

              <section id="information" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
                <p className="text-gray-700 mb-4">
                  We collect several types of information from and about users of our platform:
                </p>

                <h3 className="text-xl font-medium mb-2">Personal Information</h3>
                <p className="text-gray-700 mb-4">
                  When you register for an account, book a property, list a property, or contact us, we may collect
                  personal information such as:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li>Name, email address, phone number, and profile picture</li>
                  <li>Billing and payment information</li>
                  <li>Government-issued identification documents (for verification purposes)</li>
                  <li>Communication between guests and property owners</li>
                  <li>Reviews and ratings</li>
                </ul>

                <h3 className="text-xl font-medium mb-2">Usage Information</h3>
                <p className="text-gray-700 mb-4">
                  We automatically collect certain information about your device and how you interact with our platform:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li>IP address, browser type, operating system, and device information</li>
                  <li>Pages you view, links you click, and actions you take on our platform</li>
                  <li>Search terms you use</li>
                  <li>Referral URLs and how you came to our platform</li>
                  <li>Time spent on our platform and frequency of visits</li>
                </ul>

                <h3 className="text-xl font-medium mb-2">Location Information</h3>
                <p className="text-gray-700 mb-4">
                  With your consent, we may collect precise location information from your device to provide
                  location-based services, such as showing nearby properties or providing directions.
                </p>
              </section>

              <section id="usage" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">3. How We Use Information</h2>
                <p className="text-gray-700 mb-4">We use the information we collect for various purposes, including:</p>

                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li>Providing, maintaining, and improving our services</li>
                  <li>Processing and managing bookings, payments, and refunds</li>
                  <li>Facilitating communication between guests and property owners</li>
                  <li>Verifying user identity and preventing fraud</li>
                  <li>Personalizing your experience and showing relevant content</li>
                  <li>Sending you service-related notifications and updates</li>
                  <li>Marketing our services and sending promotional materials (with your consent)</li>
                  <li>Analyzing usage patterns to improve our platform</li>
                  <li>Complying with legal obligations</li>
                </ul>
              </section>

              <section id="sharing" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">4. Information Sharing</h2>
                <p className="text-gray-700 mb-4">
                  We may share your information with the following categories of recipients:
                </p>

                <h3 className="text-xl font-medium mb-2">Other Users</h3>
                <p className="text-gray-700 mb-4">
                  When you book a property, certain information is shared with the property owner, such as your name,
                  profile picture, and booking details. Similarly, when you list a property, certain information is
                  shared with potential guests.
                </p>

                <h3 className="text-xl font-medium mb-2">Service Providers</h3>
                <p className="text-gray-700 mb-4">
                  We may share your information with third-party service providers who perform services on our behalf,
                  such as payment processing, data analysis, email delivery, hosting, customer service, and marketing
                  assistance.
                </p>

                <h3 className="text-xl font-medium mb-2">Legal Requirements</h3>
                <p className="text-gray-700 mb-4">
                  We may disclose your information if required to do so by law or in response to valid requests by
                  public authorities (e.g., a court or government agency).
                </p>

                <h3 className="text-xl font-medium mb-2">Business Transfers</h3>
                <p className="text-gray-700 mb-4">
                  If we are involved in a merger, acquisition, or sale of all or a portion of our assets, your
                  information may be transferred as part of that transaction.
                </p>

                <h3 className="text-xl font-medium mb-2">With Your Consent</h3>
                <p className="text-gray-700 mb-4">
                  We may share your information with third parties when you have given us your consent to do so.
                </p>
              </section>

              <section id="cookies" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">5. Cookies & Tracking</h2>
                <p className="text-gray-700 mb-4">
                  We use cookies and similar tracking technologies to track activity on our platform and hold certain
                  information. Cookies are files with a small amount of data that may include an anonymous unique
                  identifier.
                </p>
                <p className="text-gray-700 mb-4">We use the following types of cookies:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li>
                    <strong>Essential Cookies:</strong> Necessary for the operation of our platform.
                  </li>
                  <li>
                    <strong>Analytical/Performance Cookies:</strong> Allow us to recognize and count the number of
                    visitors and see how visitors move around our platform.
                  </li>
                  <li>
                    <strong>Functionality Cookies:</strong> Enable us to personalize content and remember your
                    preferences.
                  </li>
                  <li>
                    <strong>Targeting Cookies:</strong> Record your visit to our platform, the pages you have visited,
                    and the links you have followed to deliver more relevant advertisements.
                  </li>
                </ul>
                <p className="text-gray-700 mb-4">
                  You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
                  However, if you do not accept cookies, you may not be able to use some portions of our platform.
                </p>
              </section>

              <section id="security" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">6. Data Security</h2>
                <p className="text-gray-700 mb-4">
                  We implement appropriate technical and organizational measures to protect the security of your
                  personal information. However, please be aware that no method of transmission over the internet or
                  method of electronic storage is 100% secure.
                </p>
                <p className="text-gray-700 mb-4">
                  We take steps to ensure that your information is treated securely and in accordance with this Privacy
                  Policy. These steps include:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li>Using encryption to protect sensitive information transmitted online</li>
                  <li>Protecting your information offline by keeping it on secure servers</li>
                  <li>
                    Limiting access to personal information to employees, contractors, and agents who need to know that
                    information
                  </li>
                  <li>Conducting regular security assessments and audits</li>
                </ul>
              </section>

              <section id="rights" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">7. Your Rights</h2>
                <p className="text-gray-700 mb-4">
                  Depending on your location, you may have certain rights regarding your personal information:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li>
                    <strong>Access:</strong> You can request access to your personal information.
                  </li>
                  <li>
                    <strong>Correction:</strong> You can request that we correct inaccurate or incomplete information.
                  </li>
                  <li>
                    <strong>Deletion:</strong> You can request that we delete your personal information.
                  </li>
                  <li>
                    <strong>Restriction:</strong> You can request that we restrict the processing of your information.
                  </li>
                  <li>
                    <strong>Data Portability:</strong> You can request a copy of your information in a structured,
                    commonly used, and machine-readable format.
                  </li>
                  <li>
                    <strong>Objection:</strong> You can object to our processing of your information.
                  </li>
                  <li>
                    <strong>Withdraw Consent:</strong> You can withdraw your consent at any time where we rely on
                    consent to process your information.
                  </li>
                </ul>
                <p className="text-gray-700 mb-4">
                  To exercise these rights, please contact us using the information provided in the "Contact Us"
                  section.
                </p>
              </section>

              <section id="children" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">8. Children's Privacy</h2>
                <p className="text-gray-700 mb-4">
                  Our platform is not intended for children under the age of 18. We do not knowingly collect personal
                  information from children under 18. If you are a parent or guardian and you believe your child has
                  provided us with personal information, please contact us.
                </p>
              </section>

              <section id="international" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">9. International Transfers</h2>
                <p className="text-gray-700 mb-4">
                  Your information may be transferred to, and maintained on, computers located outside of your state,
                  province, country, or other governmental jurisdiction where the data protection laws may differ from
                  those in your jurisdiction.
                </p>
                <p className="text-gray-700 mb-4">
                  If you are located outside India and choose to provide information to us, please note that we transfer
                  the information, including personal information, to India and process it there.
                </p>
                <p className="text-gray-700 mb-4">
                  Your consent to this Privacy Policy followed by your submission of such information represents your
                  agreement to that transfer.
                </p>
              </section>

              <section id="changes" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">10. Changes to Policy</h2>
                <p className="text-gray-700 mb-4">
                  We may update our Privacy Policy from time to time. We will notify you of any changes by posting the
                  new Privacy Policy on this page and updating the "Last Updated" date.
                </p>
                <p className="text-gray-700 mb-4">
                  You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy
                  Policy are effective when they are posted on this page.
                </p>
              </section>

              <section id="contact" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">11. Contact Us</h2>
                <p className="text-gray-700 mb-4">
                  If you have any questions about this Privacy Policy, please contact us at:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700">VillaKosh</p>
                  <p className="text-gray-700">123 Villa Street</p>
                  <p className="text-gray-700">Mumbai, India</p>
                  <p className="text-gray-700">Email: privacy@villakosh.com</p>
                  <p className="text-gray-700">Phone: +91 9876543210</p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </motion.main>

      {/* Footer for desktop */}
      {!isMobile && <Footer />}

      {/* Mobile Bottom Nav */}
      {isMobile && <MobileBottomNav user={user} />}
    </div>
  )
}

